/* Native Ajax Cart requests and browser-only customization drafts. Never stores cart tokens. */
(() => {
  if (window.BljCartFlow) return;
  const problem = code => Object.assign(new Error(code), { code });
  class BrowserRepository {
    open() {
      if (this.promise) return this.promise;
      this.promise = new Promise((resolve, reject) => {
        if (!window.indexedDB) return reject(problem('BLJ_STORAGE'));
        const request = indexedDB.open('benlujiang-customization-v1', 1);
        request.onupgradeneeded = () => {
          request.result.createObjectStore('requests', { keyPath: 'key' });
          const drafts = request.result.createObjectStore('drafts', { keyPath: 'key' });
          drafts.createIndex('expiresAt', 'expiresAt');
        };
        request.onerror = () => reject(problem('BLJ_STORAGE'));
        request.onblocked = () => reject(problem('BLJ_STORAGE'));
        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => db.close();
          const tx = db.transaction('drafts', 'readwrite');
          const cursor = tx.objectStore('drafts').index('expiresAt').openCursor(IDBKeyRange.upperBound(Date.now()));
          cursor.onsuccess = () => { const c = cursor.result; if (c) { c.delete(); c.continue(); } };
          resolve(db);
        };
      });
      return this.promise;
    }
    async change(store, key, transform) {
      const db = await this.open();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite'), table = tx.objectStore(store);
        let result;
        const get = table.get(key);
        get.onsuccess = () => {
          try {
            const next = transform(get.result);
            result = next.result;
            if (next.remove) table.delete(key);
            else if (next.value) table.put(next.value);
          } catch (error) { tx.abort(); reject(error); }
        };
        tx.oncomplete = () => resolve(result);
        tx.onerror = tx.onabort = () => reject(problem('BLJ_STORAGE'));
      });
    }
    async get(store, key) { return this.change(store, key, value => ({ result: value })); }
  }
  function create(options = {}) {
    const repo = options.repo || new BrowserRepository(), transport = options.fetch || ((...args) => fetch(...args));
    const clock = options.now || Date.now, uuid = options.uuid || (() => crypto.randomUUID());
    const digest = options.digest || (async bytes => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))).map(b => b.toString(16).padStart(2, '0')).join(''));
    async function cart() {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await transport((window.routes.cart_url || '/cart').replace(/\/$/, '') + '.js', { cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw problem('BLJ_CART_READ');
        const value = await response.json();
        if (!Array.isArray(value.items)) throw problem('BLJ_CART_READ');
        if (value.currency !== 'CNY') throw problem('BLJ_CART_CURRENCY');
        return { items: value.items, currency: value.currency };
      } catch (error) {
        if (error?.code === 'BLJ_CART_READ' || error?.code === 'BLJ_CART_CURRENCY') throw error;
        throw problem('BLJ_CART_READ');
      } finally { clearTimeout(timer); }
    }
    async function transition(context, state) {
      try {
        return await repo.change('requests', context.key, previous => {
          if (previous?.requestId !== context.requestId) return { result: false };
          return { value: { ...previous, state, updatedAt: clock() }, result: true };
        });
      } catch (error) {
        if (['committed', 'unknown', 'rejected'].includes(state)) throw problem('BLJ_UNKNOWN');
        throw error;
      }
    }
    function customProperty(read, currentKey, legacyKey) {
      const usable = value => typeof value === 'string' ? value.trim() : value?.size ? value : null;
      const current = usable(read(currentKey)), legacy = usable(read(legacyKey));
      if (current && legacy && current !== legacy) throw problem('BLJ_REVIEW');
      return current || legacy;
    }
    async function add(formData, config) {
      const variant = String(formData.get('id')), quantity = Number(formData.get('quantity') || 1);
      const readForm = key => formData.get('properties[' + key + ']');
      const file = customProperty(readForm, '参考照片', 'Customization photo'), notes = String(customProperty(readForm, '定制说明', 'Customization notes') || '').trim();
      const fileHash = file?.size ? await digest(await file.arrayBuffer()) : '';
      const key = await digest(new TextEncoder().encode(JSON.stringify([variant, notes, fileHash])));
      const context = await repo.change('requests', key, previous => {
        const busy = previous && ['checking', 'sending', 'unknown', 'acknowledged'].includes(previous.state);
        const staleCheck = previous?.state === 'checking' && clock() - previous.updatedAt > 30000;
        if (busy && !staleCheck) return { result: { ...previous, owner: false } };
        const value = { key, requestId: uuid(), variant, quantity, state: 'checking', updatedAt: clock() };
        return { value, result: { ...value, owner: true } };
      });
      let before;
      try { before = await cart(); }
      catch (error) { if (context.owner) await transition(context, 'not_sent'); throw error; }
      const matching = value => value.items.filter(item => String(item.variant_id) === variant && item.properties?._BLJ_customization_id === key);
      const requiredData = item => {
        const properties = item.properties || {}, read = key => properties[key];
        const actualPhoto = customProperty(read, '参考照片', 'Customization photo');
        const photoReceived = typeof actualPhoto === 'string' && /^(https:\/\/|\/\/|\/uploads\/)/.test(actualPhoto);
        return String(customProperty(read, '定制说明', 'Customization notes') || '').trim() === notes && (file?.size ? photoReceived : !actualPhoto);
      };
      if (matching(before).length) {
        await transition(context, 'committed');
        throw problem(matching(before).length === 1 && matching(before).every(requiredData) ? 'BLJ_ALREADY' : 'BLJ_REVIEW');
      }
      if (!context.owner) throw problem('BLJ_UNKNOWN');
      if (!await transition(context, 'sending')) throw problem('BLJ_UNKNOWN');
      formData.set('properties[_BLJ_customization_id]', key);
      formData.set('properties[_BLJ_request_id]', context.requestId);
      let response, payload, failure;
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 30000);
      try {
        response = await transport(window.routes.cart_add_url, { ...config, body: formData, signal: controller.signal });
        payload = await response.json();
      } catch (error) { failure = error; }
      finally { clearTimeout(timer); }
      let after;
      try { after = await cart(); }
      catch { await transition(context, 'unknown'); throw problem('BLJ_UNKNOWN'); }
      const rows = matching(after);
      if (rows.length) {
        await transition(context, 'committed');
        if (rows.length !== 1 || rows[0].quantity !== quantity || !rows.every(requiredData)) throw problem('BLJ_REVIEW');
        if (failure || !response?.ok || payload?.status) throw problem('BLJ_ALREADY');
        return payload;
      }
      if (!failure && response?.status >= 400 && response.status < 500 && payload?.status) {
        await transition(context, 'rejected');
        return payload;
      }
      await transition(context, 'unknown');
      throw problem('BLJ_UNKNOWN');
    }
    let session;
    function draftKey(product, variant) {
      if (!session) {
        try { session = sessionStorage.getItem('blj-draft-session') || uuid(); sessionStorage.setItem('blj-draft-session', session); }
        catch { throw problem('BLJ_STORAGE'); }
      }
      return session + ':' + product + ':' + variant;
    }
    return {
      add, draftKey,
      getDraft: async key => {
        const draft = await repo.get('drafts', key);
        if (draft?.expiresAt > clock()) return draft;
        if (draft) await repo.change('drafts', key, () => ({ remove: true }));
        return null;
      },
      saveDraft: (key, value) => repo.change('drafts', key, () => ({ value: { ...value, key, expiresAt: clock() + 86400000 }, result: true })),
      clearDraft: key => repo.change('drafts', key, () => ({ remove: true, result: true })),
      create
    };
  }
  window.BljCartFlow = create();
})();

